import { createSignal, onMount, For, createMemo, Show } from "solid-js";
import { EmptyState } from '../components/EmptyState';
import { Pagination } from '../components/Pagination';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import { useItemName } from '../hooks/useItemName';
import { useTranslation } from '../hooks/useTranslation';
import { JobEntry, JobOverviewItem } from '../models/JobEntry';
import { useUserContext } from '../components/UserContextProvider';
import ItemStackView from '../components/ItemStackView';
import { fromIdentifier } from '../services/itemRefAdapters';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { useTablePref } from '../hooks/useTablePrefs';
import { formatDate as formatDateHelper } from '../services/dateFormat';
import { formatCurrency, formatInteger } from '../services/numberFormat';
import styles from './Jobs.module.css';

const PAGE_SIZE = 60;

export default function Jobs() {
    const api = useApi();
    const toast = useToast();
    useUserContext();
    const resolver = useCraftingResolver();
    const { t } = useTranslation();
    const { itemName } = useItemName();

    const [jobEntries, setJobEntries] = createSignal<JobEntry[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [searchTerm, setSearchTerm] = useTablePref<string>("jobs.search", "");
    const [sortBy, setSortBy] = useTablePref<"scan" | "latest" | "name" | "date">("jobs.sort.v2", "scan");
    const [page, setPage] = createSignal(0);

    onMount(async () => {
        await fetchJobEntries();
    });

    async function fetchJobEntries() {
        try {
            setLoading(true);
            const response = await api.jobEntry.getAllJobEntries();
            if (isOk(response.data)) {
                setJobEntries((response.data.value.entries ?? []) as JobEntry[]);
            } else {
                toast?.error(t("JOBS_SCAN_FAILED"));
            }
        } catch {
            toast?.error(t("JOBS_SCAN_FAILED"));
        } finally {
            setLoading(false);
        }
    }

    const overviewItems = createMemo<JobOverviewItem[]>(() => {
        const entries = jobEntries();
        const itemMap: Record<string, JobOverviewItem> = {};

        entries.forEach(entry => {
            const key = entry.isCustomBlock
                ? `cb:${entry.name}:${entry.itemIdentifier}`
                : `${entry.itemIdentifier}:${entry.metadata}`;

            const prices = Object.keys(entry.pricesToStacks).map(k => parseInt(k));
            if (prices.length === 0) return;
            const entryHighest = Math.max(...prices);
            const entryStacksAtHighest = entry.pricesToStacks[entryHighest] || 0;

            const existing = itemMap[key];
            const entrySeq = entry.sequenceNumber ?? Number.MAX_SAFE_INTEGER;
            if (!existing) {
                itemMap[key] = {
                    itemIdentifier: entry.itemIdentifier,
                    metadata: entry.metadata,
                    highestPrice: entryHighest,
                    stacksAtHighestPrice: entryStacksAtHighest,
                    latestEntry: entry.createdAt,
                    name: entry.name,
                    isCustomBlock: entry.isCustomBlock,
                    scanOrder: entrySeq,
                };
            } else {
                if (entryHighest > existing.highestPrice) {
                    existing.highestPrice = entryHighest;
                    existing.stacksAtHighestPrice = entryStacksAtHighest;
                }
                if (entry.createdAt > existing.latestEntry) {
                    existing.latestEntry = entry.createdAt;
                    existing.scanOrder = entrySeq;
                }
            }
        });

        return Object.values(itemMap);
    });

    const filteredItems = createMemo(() => {
        let items = overviewItems();
        const term = searchTerm().trim().toLowerCase();
        if (term) {
            items = items.filter(item => {
                const displayName = getItemDisplayName(item).toLowerCase();
                return item.itemIdentifier.toLowerCase().includes(term) ||
                       displayName.includes(term) ||
                       item.name.toLowerCase().includes(term);
            });
        }

        const sorted = [...items];
        switch (sortBy()) {
            case "name":
                sorted.sort((a, b) => getItemDisplayName(a).localeCompare(getItemDisplayName(b)));
                break;
            case "date":
                sorted.sort((a, b) => new Date(b.latestEntry).getTime() - new Date(a.latestEntry).getTime());
                break;
            case "latest":
                sorted.sort((a, b) => new Date(b.latestEntry).getTime() - new Date(a.latestEntry).getTime());
                break;
            case "scan":
            default:
                sorted.sort((a, b) => a.scanOrder - b.scanOrder);
                break;
        }
        return sorted;
    });

    const totalPages = createMemo(() => Math.max(1, Math.ceil(filteredItems().length / PAGE_SIZE)));

    const pagedItems = createMemo(() => {
        const items = filteredItems();
        const p = Math.min(page(), totalPages() - 1);
        const start = p * PAGE_SIZE;
        return items.slice(start, start + PAGE_SIZE);
    });

    function getItemDisplayName(item: JobOverviewItem): string {
        if (item.isCustomBlock) return item.name;
        const r = resolver();
        if (r) {
            const bare = item.itemIdentifier.replace(/^minecraft:/, '');
            const match = r.getAllItems?.()?.find(i => i.name === bare && i.metadata === item.metadata)
                       ?? r.getAllItems?.()?.find(i => i.name === bare);
            if (match) return itemName(match.key);
        }
        return item.name || item.itemIdentifier;
    }

    function buildDetailsHref(item: JobOverviewItem): string {
        const idSegment = item.itemIdentifier && item.itemIdentifier.length > 0
            ? item.itemIdentifier
            : '_';
        const nameSegment = item.name && item.name.length > 0
            ? item.name
            : '_';
        const query = item.metadata ? `?m=${item.metadata}` : '';
        const search = window.location.search;
        const previewFlag = search.includes('preview=true')
            ? (query ? '&preview=true' : '?preview=true')
            : '';
        return `/jobs/${encodeURIComponent(idSegment)}/${encodeURIComponent(nameSegment)}${query}${previewFlag}`;
    }

    return (
        <div class={styles.container}>
            <div class={styles.header}>
                <div class={styles.titleSection}>
                    <div class={styles.titleRow}>
                        <h1><Text val="JOBS_TITLE" /></h1>
                        <Show when={!loading()}>
                            <span class={styles.entryCount}>
                                {formatInteger(filteredItems().length)} <Text val="JOBS_ENTRIES_COUNT" />
                            </span>
                        </Show>
                    </div>
                    <p class={styles.subtitle}>
                        <Text val="JOBS_SUBTITLE" />
                    </p>
                </div>
            </div>

            <div class={styles.searchControls}>
                <div class={styles.searchWrapper}>
                    <i class="fa-solid fa-magnifying-glass"></i>
                    <input
                        type="text"
                        class={styles.searchInput}
                        placeholder={t("JOBS_SEARCH_PLACEHOLDER")}
                        value={searchTerm()}
                        onInput={(e) => { setSearchTerm((e.target as HTMLInputElement).value); setPage(0); }}
                    />
                </div>
                <select
                    class={styles.sortSelect}
                    value={sortBy()}
                    onChange={(e) => { setSortBy(e.target.value as "scan" | "latest" | "name" | "date"); setPage(0); }}
                >
                    <option value="scan">{t("JOBS_SORT_SCAN")}</option>
                    <option value="latest">{t("JOBS_SORT_LATEST")}</option>
                    <option value="name">{t("JOBS_SORT_NAME")}</option>
                    <option value="date">{t("JOBS_SORT_DATE")}</option>
                </select>
            </div>

            <Show
                when={!loading()}
                fallback={
                    <div class={`${styles.grid} list-stagger`} aria-busy="true">
                        <For each={Array(8).fill(0)}>
                            {() => (
                                <div class={`${styles.jobCard} ${styles.skeletonCard}`}>
                                    <div class={styles.cardHeader}>
                                        <div class={`${styles.itemIcon} ${styles.skeletonShimmer}`}></div>
                                        <div class={styles.itemInfo}>
                                            <div class={`${styles.skeletonLine} ${styles.skeletonShimmer}`} style={{ width: '60%' }}></div>
                                            <div class={`${styles.skeletonLine} ${styles.skeletonShimmer}`} style={{ width: '40%' }}></div>
                                        </div>
                                    </div>
                                    <div class={styles.statsGrid}>
                                        <div class={`${styles.statTile} ${styles.skeletonShimmer}`} style={{ height: '4.5rem' }}></div>
                                        <div class={`${styles.statTile} ${styles.skeletonShimmer}`} style={{ height: '4.5rem' }}></div>
                                    </div>
                                </div>
                            )}
                        </For>
                    </div>
                }
            >
                <Show
                    when={filteredItems().length > 0}
                    fallback={
                        <EmptyState illustration="jobs" title={t('JOBS_EMPTY_TITLE')} hint={t('JOBS_EMPTY_DESC')} />
                    }
                >
                    <div class={`${styles.grid} list-stagger`}>
                        <For each={pagedItems()}>
                            {(item, index) => (
                                <a
                                    href={buildDetailsHref(item)}
                                    class={`${styles.jobCard}${item.isCustomBlock ? ` ${styles.customBlock}` : ''}`}
                                    style={{ '--i': index() } as any}
                                >
                                    <Show when={item.isCustomBlock}>
                                        <span class={styles.customBadge}>
                                            <i class="fa-solid fa-cubes-stacked"></i>
                                            <Text val="CUSTOM_BLOCK" />
                                        </span>
                                    </Show>

                                    <div class={styles.cardHeader}>
                                        <div class={styles.itemIcon}>
                                            <ItemStackView stack={fromIdentifier(item.itemIdentifier, item.metadata, resolver())} />
                                        </div>
                                        <div class={styles.itemInfo}>
                                            <h3>{getItemDisplayName(item)}</h3>
                                            <Show when={item.isCustomBlock && item.name && item.name !== getItemDisplayName(item)}>
                                                <p class={styles.itemName}>{item.name}</p>
                                            </Show>
                                        </div>
                                    </div>

                                    <div class={styles.statsGrid}>
                                        <div class={styles.statTile}>
                                            <span class={styles.statLabel}>
                                                <Text val="HIGHEST_PRICE_SHORT" />
                                            </span>
                                            <span class={styles.statValue}>
                                                {formatCurrency(item.highestPrice)}
                                            </span>
                                        </div>
                                        <div class={styles.statTile}>
                                            <span class={styles.statLabel}>
                                                <Text val="STACKS_AT_HIGHEST" />
                                            </span>
                                            <span class={styles.statValueAlt}>
                                                {formatInteger(item.stacksAtHighestPrice)}
                                            </span>
                                        </div>
                                    </div>

                                    <div class={styles.cardFooter}>
                                        <span class={styles.latestDate}>
                                            <i class="fa-solid fa-clock-rotate-left"></i>
                                            {formatDateHelper(item.latestEntry)}
                                        </span>
                                        <span class={styles.viewDetails}>
                                            <Text val="VIEW_DETAILS" />
                                            <i class="fa-solid fa-arrow-right"></i>
                                        </span>
                                    </div>
                                </a>
                            )}
                        </For>
                    </div>

                    <Pagination page={page()} totalPages={totalPages()} onChange={setPage} size="sm" />
                </Show>
            </Show>
        </div>
    );
}
